import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface PatientDetail {
  id: string
  full_name: string
  birth_date: string | null
  address: string | null
  emergency_contact: string | null
  emergency_contact_country_code: string | null
  condition_description: string | null
  medical_history: string | null
  current_medications: string[] | null
  status: 'active' | 'inactive' | 'discharged' | 'deleted'
  organization_id: string
  created_at: string
  updated_at: string
  family_members: Array<{
    id: string
    relationship: string
    whatsapp_number: string | null
    country_code: string
    is_primary_contact: boolean
    can_report: boolean
    profiles: {
      full_name: string
      phone: string | null
    }
  }>
  _count: {
    reports: number
    insights: number
    summaries: number
  }
}

async function fetchPatientDetail(patientId: string): Promise<PatientDetail> {
  // Fetch patient with family members and their profiles
  const { data: patient, error: patientError } = await supabase
    .from('patients')
    .select(`
      *,
      family_members (
        id,
        relationship,
        whatsapp_number,
        country_code,
        is_primary_contact,
        can_report,
        profiles (
          full_name,
          phone
        )
      )
    `)
    .eq('id', patientId)
    .single()

  if (patientError) throw patientError

  // Fetch counts
  const [reportsCount, insightsCount, summariesCount] = await Promise.all([
    supabase
      .from('daily_reports')
      .select('*', { count: 'exact', head: true })
      .eq('patient_id', patientId),
    supabase
      .from('key_insights')
      .select('*', { count: 'exact', head: true })
      .eq('patient_id', patientId),
    supabase
      .from('weekly_summaries')
      .select('*', { count: 'exact', head: true })
      .eq('patient_id', patientId),
  ])

  return {
    ...patient,
    _count: {
      reports: reportsCount.count ?? 0,
      insights: insightsCount.count ?? 0,
      summaries: summariesCount.count ?? 0,
    },
  } as PatientDetail
}

export function usePatientDetail(patientId: string) {
  return useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => fetchPatientDetail(patientId),
    enabled: !!patientId,
  })
}

// Fetch patient reports with reporter info (family member + profile)
export function usePatientReports(patientId: string) {
  return useQuery({
    queryKey: ['patient-reports', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_reports')
        .select(`
          *,
          reporter:family_members (
            id,
            relationship,
            profiles (
              full_name
            )
          )
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      return data
    },
    enabled: !!patientId,
  })
}

// Fetch patient insights
export interface PatientInsight {
  id: string
  insight_text: string
  category: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  ai_confidence: number
  requires_attention: boolean
  acknowledged_by: string | null
  acknowledged_at: string | null
  created_at: string
  // Mapped fields for component compatibility
  content: string
  type: string
  confidence: number
  acknowledged: boolean
}

export function usePatientInsights(patientId: string) {
  return useQuery({
    queryKey: ['patient-insights', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('key_insights')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error

      // Map to include friendly fields for components
      return (data ?? []).map((insight) => ({
        ...insight,
        content: insight.insight_text,
        type: insight.category,
        confidence: insight.ai_confidence,
        acknowledged: !!insight.acknowledged_at,
      })) as PatientInsight[]
    },
    enabled: !!patientId,
  })
}

// Fetch patient weekly summaries
export function usePatientSummaries(patientId: string) {
  return useQuery({
    queryKey: ['patient-summaries', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('weekly_summaries')
        .select('*')
        .eq('patient_id', patientId)
        .order('week_end_date', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    enabled: !!patientId,
  })
}
